import { sharedCookieDomain, normalizeLocale, DEFAULT_LOCALE } from '@self-restaurant/shared'
/**
 * i18n do painel administrativo — dicionário do banco com fallback local.
 *
 * A fonte de verdade é a tabela ui_texts (chaves `admin.*`), servida por
 * GET /admin/i18n/dictionary com cascata exato → base → en. O fallback
 * embutido (i18n/source.ts) cobre a tela de login (endpoint autenticado)
 * e o instante antes do primeiro fetch — o painel nunca exibe chave crua.
 * Idioma escolhido persiste em localStorage (sr_admin_lang).
 * #419: códigos SEM região — valores legados de cookie/localStorage
 * ('pt-BR', 'pt-PT', 'en-US'…) são normalizados na leitura e regravados.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import type { ReactNode } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { i18n } from './api.js'
import { ADMIN_PT, ADMIN_EN } from '../i18n/source.js'
import { onTokenChange } from './auth.js'
import { dictionaryContext, onTenantGateChange } from './session.js'

function subscribeDictionaryContext(fn: () => void): () => void {
  const offToken = onTokenChange(fn)
  const offGate = onTenantGateChange(fn)
  return () => {
    offToken()
    offGate()
  }
}

const LANG_KEY = 'sr_admin_lang'

/** Idiomas com fallback embutido no bundle — cobertura imediata. */
const BUNDLED_BASES: Record<string, Record<string, string>> = { pt: ADMIN_PT, en: ADMIN_EN }

/** Fallback embutido do idioma: base (pt/en) > en — línguas sem bundle
 * (fr, de, ja, ar…) caem no inglês, a língua-ponte da plataforma, com a
 * mesma cascata do dicionário servido (exato → base → en). */
export function bundledFallback(lang: string): Record<string, string> {
  const base = normalizeLocale(lang)
  return BUNDLED_BASES[base] ?? ADMIN_EN
}

/** Idioma inicial a partir do navigator.language — sempre código base
 * (#419): 'pt-PT'/'pt-BR' → 'pt', 'en-US'/'en-GB' → 'en'. */
export function langFromNavigator(navLang: string): string {
  const nav = navLang.toLowerCase()
  if (!nav) return DEFAULT_LOCALE
  return normalizeLocale(nav)
}

// ─── preferência cross-app (landing · painel · cardápio) ─────────────────────
// Cookie sr_lang no domínio pai (pratzu.com / asknamba.com): o idioma escolhido em
// QUALQUER um dos três apps vale para os outros. Em dev (localhost) o cookie
// é host-only — portas diferentes do mesmo host já o compartilham.
const LANG_COOKIE = 'sr_lang'

function cookieDomain(): string | undefined {
  if (typeof location === 'undefined') return undefined
  const host = location.hostname
  return sharedCookieDomain(host)
}

function getSharedLang(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LANG_COOKIE}=([^;]+)`))
  return match ? decodeURIComponent(match[1]!) : null
}

function setSharedLang(lang: string): void {
  if (typeof document === 'undefined') return
  const parts = [
    `${LANG_COOKIE}=${encodeURIComponent(lang)}`,
    'path=/',
    'max-age=31536000',
    'samesite=lax',
  ]
  const domain = cookieDomain()
  if (domain) parts.push(`domain=${domain}`, 'secure')
  document.cookie = parts.join('; ')
}

/** #419: valor legado de cookie/localStorage ('pt-BR'…) normalizado na
 *  leitura; se mudou, regrava já sem região. */
function normalizeStoredLang(raw: string | null): string | null {
  if (!raw) return null
  const normalized = normalizeLocale(raw)
  if (normalized !== raw) {
    try {
      localStorage.setItem(LANG_KEY, normalized)
    } catch {
      /* storage indisponível — valor só nesta sessão */
    }
    setSharedLang(normalized)
  }
  return normalized
}

export function detectAdminLang(): string {
  // 0. escolha feita em qualquer app (landing/painel/cardápio)
  const shared = normalizeLocale(getSharedLang() ?? '')
  if (shared) return shared
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  try {
    const saved = normalizeStoredLang(localStorage.getItem(LANG_KEY))
    if (saved) return saved
  } catch {
    /* storage indisponível — detecção abaixo */
  }
  return langFromNavigator(navigator.language ?? '')
}

type I18nContextValue = {
  lang: string
  setLang: (lang: string) => void
  t: (key: string, vars?: Record<string, string>) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function AdminI18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState(detectAdminLang)

  // Sem token: dicionário PÚBLICO da tela de login (só chaves admin.login.*)
  // — a tela de entrada fica coberta em todos os idiomas do catálogo antes do
  // primeiro acesso. Com token: dicionário completo do painel.
  // #319: em /r/:slug só pede o dicionário com um token DESTE tenant (antes,
  // o token de outro restaurante dava 403 e o erro ficava em cache); a troca
  // de tenant (refresh/select do guard) pede o dicionário do tenant novo.
  const context = useSyncExternalStore(subscribeDictionaryContext, dictionaryContext)
  const { data } = useQuery({
    queryKey: ['admin-dict', lang, context],
    queryFn: () => (context === 'login' ? i18n.loginDictionary(lang) : i18n.dictionary(lang)),
    enabled: context !== 'wait',
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    retry: 1,
  })

  const setLang = useCallback((next: string) => {
    // #419: grava sempre o código sem região
    const lang = normalizeLocale(next)
    setLangState(lang)
    // preferência cross-app (vale para landing e cardápio) + legado do painel
    setSharedLang(lang)
    try {
      localStorage.setItem(LANG_KEY, lang)
    } catch {
      /* storage indisponível — escolha só nesta sessão */
    }
  }, [])

  // dicionário do banco > fallback embutido da base > chave
  const t = useCallback(
    (key: string, vars?: Record<string, string>) => {
      const fallback = bundledFallback(lang)
      let text = data?.values[key] ?? fallback[key] ?? key
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.replaceAll(`{${name}}`, value)
        }
      }
      return text
    },
    [data, lang],
  )

  // <html lang> acompanha a escolha (leitores de tela, tradutores nativos)
  useEffect(() => {
    document.documentElement.lang = lang
    // árabe (e outras RTL): direção acompanha o idioma; fontes via :lang() nos tokens
    document.documentElement.dir = /^(ar|he|fa|ur)(-|$)/i.test(lang) ? 'rtl' : 'ltr'
  }, [lang])

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n precisa estar dentro de <AdminI18nProvider>')
  return ctx
}
